import type { ChangeEventHandler } from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../state/hooks";
import type { RootState } from "../../state/store";
import { closeConfirmationModal } from "../../state/slice/modal/modalSlice";
import ModalStandard from "./ModalStandard";
import { IsLoading } from "../AppState/IsLoading";
import Spinner from "../Loader/Spinner";
import {
  MediaModules,
  type IEntitiesToDisplayWarning,
  type WarningEntity,
} from "src/features/gallery/components/MediaModuleWarning";
import {
  checkIfMediasAreUsed,
  checkMediaExists,
  replaceMedia,
} from "src/features/gallery/state/gallerySlice";
import {
  deduceMediaType,
  fromFileReturnMD5AndSize,
  fromMediaTypeInferInputAcceptParam,
} from "../../../features/gallery/state/uploadUtil";
import type { MD5AndSize } from "../../../features/gallery/state/uploadUtil";
import { fromFileReturnMD5AndSizeWorker } from "../../../features/gallery/state/uploadUtilWorkers";
import { militaryGradeClientGateAgainstSuspiciousOrSeditiousFiles } from "../../../features/gallery/utils/securityGate";
import type { GateResult, MediaKind } from "../../../features/gallery/utils/securityGateCore";
import { useMediaById } from "../../../features/gallery/useMediaById";
import type { IMedia } from "src/model/unityObject";
import { setAlert } from "src/features/alert/state/alertsSlice";
import { freshDerivedUrl } from "src/features/gallery/util/isDerivedMediaUrlStale";
import { prettifyFileSizeInBytes } from "../../util/prettifyFileSizeInBytes";
import { languageConfig } from "../../util/langConfig";
import type { ILanguage } from "src/model/model";
import { getCurrentUser } from "src/features/profile/state/profileSlice";

export const MEDIA_REPLACE_CHECK_BEFORE_ID = "mediaReplaceCheckBeforeId";
export const MEDIA_REPLACE_COMPONENT_ID = "mediaReplaceComponentId";

const REPLACEABLE_MEDIA_TYPES: readonly MediaKind[] = ["image", "video", "audio", "pdf", "model3d"];

const isReplaceableMediaType = (type: string): type is MediaKind =>
  REPLACEABLE_MEDIA_TYPES.includes(type as MediaKind);

const gateKindForMediaType = (type: string): MediaKind | undefined => {
  if (type === "avatar") return "model3d";
  return isReplaceableMediaType(type) ? type : undefined;
};

const expectedInferredTypeForMediaType = (type: string): string =>
  type === "avatar" ? "model3d" : type;

const isGateFail = (g: GateResult): g is Extract<GateResult, { ok: false }> => g.ok === false;

const safeDeduceMediaType = (extension: string): string | null => {
  try {
    return deduceMediaType(extension);
  } catch {
    return null;
  }
};

type ModalConfirmationToReplaceMediaProps = {
  id: string;
  media: IMedia;
};

const ModalConfirmationToReplaceMedia = ({ id, media }: ModalConfirmationToReplaceMediaProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isThisModalOpen = useAppSelector(
    (state: RootState) =>
      state.modals.showConfirmationModal.id === id && state.modals.showConfirmationModal.show,
  );

  const currentUser = useAppSelector(getCurrentUser);
  const currentLocale = languageConfig((currentUser?.preferred_language as ILanguage)?.name);

  const [selectedFile, setSelectedFile] = useState<File | undefined>();
  const [MD5, setMD5] = useState("");
  const [isMd5Loaded, setIsMd5Loaded] = useState(true);
  const [gateOk, setGateOk] = useState(false);
  const [typeMismatch, setTypeMismatch] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [duplicateMediaId, setDuplicateMediaId] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [entitiesToDisplayWarning, setEntitiesToDisplayWarning] =
    useState<IEntitiesToDisplayWarning>({ projects: [], users: [], organizations: [] });
  const [isUsageCheckFinished, setIsUsageCheckFinished] = useState(true);

  const { media: duplicateMedia } = useMediaById(duplicateMediaId ?? undefined);

  const [newFilePreviewUrl, setNewFilePreviewUrl] = useState<string | undefined>();

  useEffect(() => {
    if (!selectedFile) {
      setNewFilePreviewUrl(undefined);
      return undefined;
    }
    const objectUrl = URL.createObjectURL(selectedFile);
    setNewFilePreviewUrl(objectUrl);
    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [selectedFile]);

  const resetFileState = () => {
    setSelectedFile(undefined);
    setMD5("");
    setIsMd5Loaded(true);
    setGateOk(false);
    setTypeMismatch(false);
    setErrorMessage("");
    setDuplicateMediaId(null);
  };

  useEffect(() => {
    if (!isThisModalOpen) {
      resetFileState();
      setEntitiesToDisplayWarning({ projects: [], users: [], organizations: [] });
      setIsUsageCheckFinished(true);
      return;
    }

    setIsUsageCheckFinished(false);
    void dispatch(
      checkIfMediasAreUsed({
        mediaIDs: [[media]],
        isToDeleteOne: false,
        componentId: MEDIA_REPLACE_CHECK_BEFORE_ID,
      }),
    ).then((res) => {
      const entities: WarningEntity[] = [];
      if (checkIfMediasAreUsed.fulfilled.match(res)) {
        res.payload.data.forEach(([usageEntities, { mediaId }]) => {
          (usageEntities ?? []).forEach((item) => {
            if (!entities.some((proj) => proj.projectName === item.projectName)) {
              entities.push({
                apiId: item.apiId ?? "",
                fieldName: item.fieldName ?? undefined,
                projectName: item.projectName ?? undefined,
                projectMedias: [mediaId],
              });
            }
          });
        });
      }
      setEntitiesToDisplayWarning({
        projects: entities.filter((e) => e.apiId !== "user" && e.apiId !== "organization"),
        users: entities.filter((e) => e.apiId === "user"),
        organizations: entities.filter((e) => e.apiId === "organization"),
      });
      setIsUsageCheckFinished(true);
    });
  }, [isThisModalOpen, media, dispatch]);

  const onFileSelected: ChangeEventHandler<HTMLInputElement> = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    resetFileState();
    setSelectedFile(file);
    setIsMd5Loaded(false);

    const extension = file.name.split(".").pop() ?? "";
    const inferredType = safeDeduceMediaType(extension);

    if (inferredType !== expectedInferredTypeForMediaType(media.type)) {
      setTypeMismatch(true);
      setIsMd5Loaded(true);
      setErrorMessage(t("pages.gallery.replaceMedia.typeMismatch"));
      return;
    }

    const expectedKind = gateKindForMediaType(media.type);

    void militaryGradeClientGateAgainstSuspiciousOrSeditiousFiles(file, {
      expectedKind,
      maxMegapixels: 100,
      maxBytes: 2_000_000_000,
      timeoutMs: 6000,
      reencodeImage: false,
    }).then((gate) => {
      if (isGateFail(gate)) {
        setGateOk(false);
        setIsMd5Loaded(true);
        setErrorMessage(t("pages.gallery.uploadPanel.problemWithThisFile"));
        return;
      }

      setGateOk(true);
      setErrorMessage("");

      const onMd5Return = (res: MD5AndSize | null) => {
        if (!res) {
          setIsMd5Loaded(true);
          setErrorMessage(t("pages.gallery.uploadPanel.problemWithThisFile"));
          return;
        }
        setMD5(res.MD5);
        void dispatch(checkMediaExists({ md5: res.MD5 }))
          .then((existsRes) => {
            if (
              checkMediaExists.fulfilled.match(existsRes) &&
              existsRes.payload.exists &&
              existsRes.payload.id !== null &&
              existsRes.payload.id !== media.id
            ) {
              setDuplicateMediaId(existsRes.payload.id);
            } else {
              setDuplicateMediaId(null);
            }
          })
          .finally(() => setIsMd5Loaded(true));
      };

      if (window.Worker) {
        try {
          fromFileReturnMD5AndSizeWorker(file, onMd5Return);
        } catch {
          void fromFileReturnMD5AndSize(file)
            .then(onMd5Return)
            .catch(() => onMd5Return(null));
        }
      } else {
        void fromFileReturnMD5AndSize(file)
          .then(onMd5Return)
          .catch(() => onMd5Return(null));
      }
    });
  };

  const canConfirm =
    !!selectedFile && gateOk && !typeMismatch && isMd5Loaded && MD5 !== "" && !isSubmitting;

  const canShowNewFilePreview = !!selectedFile && gateOk && !typeMismatch;

  const currentImageUrl = freshDerivedUrl(media, media.thumbnail_s3_url) ?? media.s3_url;

  const currentVideoUrl =
    freshDerivedUrl(media, media.s3_url_ffmpeg_preset2 ?? media.s3_url_ffmpeg_preset_2) ??
    freshDerivedUrl(media, media.s3_url_ffmpeg_preset1 ?? media.s3_url_ffmpeg_preset_1) ??
    media.s3_url;

  const currentSourceUrl: string | undefined =
    media.type === "image"
      ? currentImageUrl
      : media.type === "video"
      ? currentVideoUrl
      : media.type === "audio" || media.type === "pdf"
      ? media.s3_url
      : undefined;

  const renderMediaPreviewByType = (
    sourceUrl: string | undefined,
    filename: string | undefined,
  ) => {
    if (media.type === "image") {
      if (!sourceUrl) return null;
      return (
        <img
          className="object-contain h-32 sm:h-48 max-w-full"
          src={sourceUrl}
          alt="media-preview"
        />
      );
    }
    if (media.type === "video") {
      if (!sourceUrl) return null;
      return (
        <video controls className="h-32 sm:h-48 max-w-full">
          <source src={sourceUrl} />
        </video>
      );
    }
    if (media.type === "audio") {
      if (!sourceUrl) return null;
      return <audio controls src={sourceUrl} className="w-full" />;
    }
    if (media.type === "pdf") {
      if (!sourceUrl) return null;
      return (
        <a
          href={sourceUrl}
          target="_blank"
          rel="noreferrer"
          className="btn-primary-fill bg-white text-green-400 rounded-full shadow-md shadow-gray-400 hover:shadow-inner hover:text-white"
        >
          {t("general.preview")}
        </a>
      );
    }
    if (!filename) return null;
    return <p className="text-sm text-gray-600 px-2 text-center break-all">{filename}</p>;
  };

  const onConfirm = () => {
    if (!canConfirm || !selectedFile) return;
    setIsSubmitting(true);
    void dispatch(
      replaceMedia({
        file: selectedFile,
        media,
        MD5,
        componentId: MEDIA_REPLACE_COMPONENT_ID,
      }),
    )
      .then((res) => {
        if (replaceMedia.fulfilled.match(res)) {
          dispatch(setAlert({ type: "success", msg: t("alerts.mediaReplaceSuccess") }));
          dispatch(closeConfirmationModal(id));
        } else if (res.payload === "canceled") {
          dispatch(closeConfirmationModal(id));
        } else {
          dispatch(
            setAlert({
              type: "danger",
              msg: res.error?.message || t("alerts.mediaReplaceFail"),
            }),
          );
        }
      })
      .finally(() => setIsSubmitting(false));
  };

  const onCancel = () => {
    dispatch(closeConfirmationModal(id));
  };

  return (
    <ModalStandard
      id={id}
      fullScreen={false}
      modalType={"confirmation"}
      className="w-2/3 h-fit max-h-[90vh] flex flex-col"
    >
      <div className="modalContent personalize-scroll-visible min-h-0 flex-1">
        <div className="z-20">
          <IsLoading componentId={MEDIA_REPLACE_COMPONENT_ID} showSuccess={false} />
        </div>
        <p className="font-bold text-2xl my-4">{t("pages.gallery.replaceMedia.title")}</p>
        <p className="text-gray-600 mb-2">{t("pages.gallery.replaceMedia.intro")}</p>
        <ul className="list-disc list-inside text-sm text-gray-600 mb-4 space-y-0.5">
          <li>{t("pages.gallery.replaceMedia.bullet1")}</li>
          <li>{t("pages.gallery.replaceMedia.bullet2")}</li>
          {(media.type === "image" || media.type === "video") && (
            <li>{t("pages.gallery.replaceMedia.bulletTranscoding")}</li>
          )}
          <li>{t("pages.gallery.replaceMedia.bullet3")}</li>
        </ul>

        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept={fromMediaTypeInferInputAcceptParam(media.type)}
          onChange={onFileSelected}
        />
        <div className="flex items-center gap-3 mb-2 min-w-0">
          <button
            type="button"
            className="btn-alternative-outline shrink-0"
            onClick={() => fileInputRef.current?.click()}
          >
            {t("pages.gallery.replaceMedia.chooseFile")}
          </button>
          {selectedFile && (
            <p className="text-sm text-gray-600 truncate min-w-0">
              {t("pages.gallery.replaceMedia.selectedFile")} : {selectedFile.name}
            </p>
          )}
        </div>

        <div className="min-h-[1.5rem]">
          {errorMessage ? (
            <p className="text-sm text-red-600">{errorMessage}</p>
          ) : duplicateMediaId !== null ? (
            <p className="text-sm text-yellow-600">
              {t("pages.gallery.replaceMedia.duplicateWarning", {
                name: duplicateMedia?.name ?? "",
              })}
            </p>
          ) : null}
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <p className="text-sm font-medium text-gray-700 mb-1">
              {t("pages.gallery.replaceMedia.currentLabel")}
            </p>
            <p className="text-xs text-gray-500 mb-1 min-h-[1rem]">
              {media.size_in_bytes
                ? prettifyFileSizeInBytes(Number(media.size_in_bytes), currentLocale)
                : ""}
            </p>
            <div className="p-2 flex items-center justify-center w-full h-32 sm:h-48 bg-gray-100 rounded-xl overflow-hidden">
              {renderMediaPreviewByType(currentSourceUrl, media.filename)}
            </div>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700 mb-1">
              {t("pages.gallery.replaceMedia.newLabel")}
            </p>
            <p className="text-xs text-gray-500 mb-1 min-h-[1rem]">
              {canShowNewFilePreview && selectedFile
                ? prettifyFileSizeInBytes(selectedFile.size, currentLocale)
                : ""}
            </p>
            <div className="p-2 flex items-center justify-center w-full h-32 sm:h-48 bg-gray-100 rounded-xl overflow-hidden">
              {canShowNewFilePreview ? (
                renderMediaPreviewByType(newFilePreviewUrl, selectedFile?.name)
              ) : (
                <p className="text-sm text-gray-500 text-center">
                  {t("pages.gallery.replaceMedia.noFileSelected")}
                </p>
              )}
            </div>
          </div>
        </div>

        <div>
          <IsLoading componentId={MEDIA_REPLACE_CHECK_BEFORE_ID} showSuccess={false}>
            {isUsageCheckFinished ? (
              <MediaModules entitiesToDisplayWarning={entitiesToDisplayWarning} severity="info" />
            ) : (
              <Spinner />
            )}
          </IsLoading>
        </div>
      </div>

      <div className="flex justify-end mt-3 shrink-0">
        <button type="button" className="btn-alternative-outline mr-4" onClick={onCancel}>
          {String(t("general.cancel"))}
        </button>
        <button
          className="btn-primary-fill"
          type="button"
          disabled={!canConfirm}
          onClick={onConfirm}
        >
          {isSubmitting ? t("general.loading") : t("pages.gallery.replaceMedia.confirmButton")}
        </button>
      </div>
    </ModalStandard>
  );
};

export default ModalConfirmationToReplaceMedia;
